import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, Check, CreditCard } from "lucide-react";
import Modal from "./Modal";
import bookingService from "../api/services/bookingService";
import { useToast } from "../hooks/useToast";

const loadPaystackScript = () =>
  new Promise((resolve, reject) => {
    if (window.PaystackPop) {
      resolve(window.PaystackPop);
      return;
    }
    const existing = document.querySelector("script[data-paystack]");
    if (existing) {
      existing.addEventListener("load", () => resolve(window.PaystackPop));
      existing.addEventListener("error", () =>
        reject(new Error("Paystack failed to load"))
      );
      return;
    }
    const script = document.createElement("script");
    script.src = "https://js.paystack.co/v1/inline.js";
    script.async = true;
    script.dataset.paystack = "true";
    script.onload = () => resolve(window.PaystackPop);
    script.onerror = () => reject(new Error("Paystack failed to load"));
    document.body.appendChild(script);
  });

// apiService.get/post already unwrap axios's response.data; our backend
// responses are shaped { success, code, message, data }, so the payload we
// actually want is one level deeper.
const unwrap = (response) => response?.data || response || {};

const METHODS = [
  {
    id: "bank-transfer",
    title: "Bank Transfer",
    description: "Transfer to a dedicated account and confirm.",
    Icon: Building2,
  },
  {
    id: "card",
    title: "Card Payment",
    description: "Pay securely with your debit or credit card.",
    Icon: CreditCard,
  },
];

// How many times (and how often) we re-check the booking status with our
// backend after the Paystack popup closes, before giving up and telling the
// user we'll confirm asynchronously. Covers webhook lag.
const VERIFY_ATTEMPTS = 5;
const VERIFY_DELAY_MS = 1800;
const PAYSTACK_ERROR_GRACE_MS = 3000;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const isPaystackScriptError = (event) => {
  if (event?.message !== "Script error.") return false;

  const filename = typeof event?.filename === "string" ? event.filename : "";
  if (filename.includes("paystack.co")) return true;

  const target = event?.target;
  const scriptSrc =
    typeof target?.getAttribute === "function"
      ? target.getAttribute("src") || ""
      : "";
  if (scriptSrc.includes("paystack.co")) return true;

  // Cross-origin runtime exceptions often arrive as only "Script error."
  // with no filename or target. While Paystack is active, treat that as
  // coming from the popup script so CRA does not show its dev overlay.
  return !filename && !scriptSrc;
};

/**
 * Lets the traveller choose how to pay after reviewing their booking.
 * Both "Card Payment" and "Bank Transfer" go through Paystack — the choice
 * only changes which Paystack channel is used:
 * - Card: Paystack Inline popup restricted to the card channel.
 * - Bank Transfer: initiates a Pay-with-Transfer charge and hands the
 *   generated account details to the caller (which renders BankTransferModal).
 */
function PaymentMethodModal({
  isOpen,
  onClose,
  onBack,
  onBankTransferReady,
  onPaymentSuccess,
  bookingDetails,
  assistantId,
  assistantName,
  totalPrice,
  userEmail,
  onWindowConflict,
}) {
  const [selectedMethod, setSelectedMethod] = useState("bank-transfer");
  const [isProcessing, setIsProcessing] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const paystackActiveRef = useRef(false);
  const paystackGraceTimeoutRef = useRef(null);
  const publicKey = process.env.REACT_APP_PAYSTACK_PUBLIC_KEY;
  const toast = useToast();

  const keepPaystackActiveBriefly = () => {
    if (paystackGraceTimeoutRef.current) {
      clearTimeout(paystackGraceTimeoutRef.current);
    }

    paystackGraceTimeoutRef.current = setTimeout(() => {
      paystackActiveRef.current = false;
      paystackGraceTimeoutRef.current = null;
    }, PAYSTACK_ERROR_GRACE_MS);
  };

  useEffect(() => {
    if (isOpen) {
      setSelectedMethod("bank-transfer");
      setIsProcessing(false);
      setAcceptedTerms(false);
    }
  }, [isOpen]);

  const buildBookingPayload = useMemo(
    () => () => ({
      assistantId,
      arrivalDate: bookingDetails?.arrivalDate,
      departureDate: bookingDetails?.departureDate,
      touristsCount: bookingDetails?.tourists || 1,
      itinerary: bookingDetails?.itinerary || "",
      escortVehicles: bookingDetails?.escortVehicles || [],
      securityPersonnel: bookingDetails?.securityPersonnel || [],
    }),
    [assistantId, bookingDetails]
  );

  // Polls our backend (which itself checks Paystack) until the booking is
  // confirmed, since the client-side Paystack callback only tells us the
  // popup finished — not that the money actually settled.
  const verifyAndFinish = async (reference, bookingId) => {
    for (let attempt = 0; attempt < VERIFY_ATTEMPTS; attempt += 1) {
      try {
        const verifyResponse = await bookingService.verifyPaystackPayment(
          reference
        );
        const { booking } = unwrap(verifyResponse);
        if (booking && booking.status !== "failed_payment") {
          if (onPaymentSuccess) onPaymentSuccess({ bookingId, booking });
          return;
        }
      } catch (err) {
        // Keep retrying — a transient verify failure shouldn't fail the
        // whole confirmation loop.
        console.warn("verifyPaystackPayment attempt failed", err);
      }
      if (attempt < VERIFY_ATTEMPTS - 1) {
        await sleep(VERIFY_DELAY_MS);
      }
    }
    toast.error(
      "We're still confirming your payment. We'll update your booking as soon as it clears — check History shortly."
    );
    if (onClose) onClose();
  };

  // The API answers 409 when someone else took part of this window while the
  // user was still choosing a payment method. Send them back to the calendar
  // with fresh availability instead of showing a generic payment failure.
  const handleWindowConflict = (err) => {
    if (err?.status !== 409) return false;
    toast.error(
      err?.message ||
        "Those hours were just booked by someone else. Please pick another time."
    );
    if (onWindowConflict) onWindowConflict();
    return true;
  };

  const handleCardPayment = async () => {
    if (!assistantId) {
      toast.error("Assistant information is missing. Please try again.");
      return;
    }
    if (!publicKey) {
      toast.error(
        "Paystack public key is missing. Add REACT_APP_PAYSTACK_PUBLIC_KEY."
      );
      return;
    }

    try {
      setIsProcessing(true);
      const initResponse = await bookingService.initPaystackPayment({
        ...buildBookingPayload(),
        channels: ["card"],
      });
      const result = unwrap(initResponse);

      if (result.walletCovered) {
        if (onPaymentSuccess) {
          onPaymentSuccess({ bookingId: result.bookingId });
        }
        return;
      }

      const { reference, accessCode, amountToCharge, bookingId } = result;
      if (!reference || !amountToCharge) {
        toast.error("Payment could not be initialized. Please try again.");
        return;
      }

      const PaystackPop = await loadPaystackScript();
      paystackActiveRef.current = true;

      const setupOptions = {
        key: publicKey,
        email: userEmail || "guest@pulo.com",
        amount: Math.round(Number(amountToCharge) * 100),
        currency: "NGN",
        ref: reference,
        channels: ["card"],
        metadata: {
          assistant: assistantName || "Pulo Assistant",
          bookingId,
        },
        callback: () => {
          keepPaystackActiveBriefly();
          verifyAndFinish(reference, bookingId);
        },
        onClose: () => {
          keepPaystackActiveBriefly();
        },
      };

      // access_code isn't a valid Inline v1 `.setup()` param — amount/ref is
      // the correct pairing for this script version, so accessCode is only
      // kept around in case a future migration to Inline v2 needs it.
      void accessCode;

      PaystackPop.setup(setupOptions).openIframe();
    } catch (err) {
      console.error("handleCardPayment error", err);
      paystackActiveRef.current = false;
      if (handleWindowConflict(err)) return;
      toast.apiError(err, "Unable to start payment. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBankTransfer = async () => {
    if (!assistantId) {
      toast.error("Assistant information is missing. Please try again.");
      return;
    }

    try {
      setIsProcessing(true);
      const initResponse = await bookingService.initBankTransfer(
        buildBookingPayload()
      );
      const result = unwrap(initResponse);

      if (result.walletCovered) {
        if (onPaymentSuccess) {
          onPaymentSuccess({ bookingId: result.bookingId });
        }
        return;
      }

      if (!result.account) {
        toast.error("Unable to generate transfer details. Please try again.");
        return;
      }

      if (onBankTransferReady) {
        onBankTransferReady({
          account: result.account,
          bookingId: result.bookingId,
          reference: result.account.reference,
        });
      }
    } catch (err) {
      console.error("handleBankTransfer error", err);
      if (handleWindowConflict(err)) return;
      toast.error(
        err?.message || "Unable to start bank transfer. Please try again."
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleContinue = () => {
    if (!acceptedTerms) {
      toast.error("Please accept the terms and conditions to continue.");
      return;
    }
    if (selectedMethod === "card") {
      handleCardPayment();
      return;
    }
    handleBankTransfer();
  };

  useEffect(() => {
    const handleWindowError = (event) => {
      if (!paystackActiveRef.current) return;
      if (!isPaystackScriptError(event)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    };

    window.addEventListener("error", handleWindowError);
    return () => {
      window.removeEventListener("error", handleWindowError);
      if (paystackGraceTimeoutRef.current) {
        clearTimeout(paystackGraceTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Modal isOpen={isOpen} onClose={onClose} panelClassName="max-w-lg">
      <div className="space-y-5">
        <div className="space-y-2 pr-10">
          <h3 className="text-lg font-bold text-[#570033]">
            Select Your Payment Method
          </h3>
          <p className="text-sm text-slate-500">
            Choose how you would like to pay for your booking.
          </p>
        </div>

        <div className="space-y-3">
          {METHODS.map(({ id, title, description, Icon }) => {
            const isSelected = selectedMethod === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => setSelectedMethod(id)}
                className={`flex w-full items-center gap-4 rounded-2xl border px-4 py-4 text-left transition ${
                  isSelected
                    ? "border-[#570033] bg-[#F8EEF4]"
                    : "border-slate-200 bg-white hover:border-slate-300"
                }`}
              >
                <span
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
                    isSelected ? "bg-[#570033] text-white" : "bg-slate-100 text-slate-500"
                  }`}
                >
                  <Icon className="h-5 w-5" />
                </span>
                <span className="flex-1">
                  <span className="block text-sm font-semibold text-slate-800">
                    {title}
                  </span>
                  <span className="block text-xs text-slate-500">
                    {description}
                  </span>
                </span>
                <span
                  className={`flex h-5 w-5 items-center justify-center rounded-full border ${
                    isSelected ? "border-[#570033]" : "border-slate-300"
                  }`}
                >
                  {isSelected ? (
                    <span className="h-2.5 w-2.5 rounded-full bg-[#570033]" />
                  ) : null}
                </span>
              </button>
            );
          })}
        </div>

        <div className="rounded-2xl bg-slate-100 px-4 py-3 text-center">
          <span className="text-base font-semibold text-[#570033]">
            Total: {totalPrice}
          </span>
        </div>

        <div className="flex items-center gap-3 rounded-xl bg-[#FFF7E6] px-4 py-3">
          <button
            type="button"
            role="checkbox"
            aria-checked={acceptedTerms}
            onClick={() => setAcceptedTerms((prev) => !prev)}
            className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-[6px] border transition ${
              acceptedTerms
                ? "border-[#C49A2B] bg-[#C49A2B] text-white"
                : "border-[#D8B45A] bg-white text-transparent"
            }`}
          >
            <Check className="h-3.5 w-3.5" strokeWidth={3} />
          </button>
          <p className="text-sm text-slate-700">
            I have read and I accept{" "}
            <Link
              to="/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-[#C49A2B] underline-offset-2 hover:underline"
            >
              terms and condition
            </Link>
          </p>
        </div>

        <button
          type="button"
          onClick={handleContinue}
          className="w-full rounded-xl bg-[#570033] py-3 text-sm font-semibold text-white transition hover:bg-[#450029] disabled:cursor-not-allowed disabled:opacity-70"
          disabled={isProcessing || !acceptedTerms}
        >
          {isProcessing ? "Starting Payment..." : "Continue"}
        </button>
        <button
          type="button"
          onClick={onBack}
          className="w-full rounded-xl border border-[#570033] py-3 text-sm font-semibold text-[#570033] transition hover:bg-[#F8EEF4]"
        >
          Go back
        </button>
      </div>
    </Modal>
  );
}

export default PaymentMethodModal;
